import React from 'react';
import dayjs from 'dayjs';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface MonthOption {
  value: string;
  label: string;
  searchLabel: string;
}

interface MonthDropdownProps {
  defaultMonth?: string;
  onSelect?: (selectedMonth: string) => void;
  className?: string;
}

const MonthDropdown: React.FC<MonthDropdownProps> = ({
  defaultMonth = '',
  onSelect,
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState<string>(defaultMonth);

  const months: MonthOption[] = React.useMemo(() => {
    const currentMonth = dayjs();
    return Array.from({ length: 12 }, (_, index) => {
      const month = currentMonth.subtract(index, 'month');
      return {
        value: month.format('YYYY-MM'),
        label: month.format('MMMM YYYY'),
        searchLabel: month.format('MMMM YYYY').toLowerCase(),
      };
    });
  }, []);

  const handleSelect = React.useCallback(
    (currentValue: string) => {
      // setValue(currentValue === value ? '' : currentValue);
      setValue(currentValue);
      setOpen(false);
      onSelect?.(currentValue);
    },
    [onSelect]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-[200px] justify-between', className)}
        >
          {value
            ? (months.find((month) => month.value === value)?.label ??
              'Select month...')
            : 'Select month...'}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder="Search month..." />
          <CommandList>
            <CommandEmpty>No month found.</CommandEmpty>
            <CommandGroup>
              {months.map((month) => (
                <CommandItem
                  key={month.value}
                  value={month.searchLabel}
                  onSelect={() => handleSelect(month.value)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === month.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {month.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default MonthDropdown;
